import { useState, useEffect, useRef, useCallback } from "react";
import {
  ClockCounterClockwise, MagnifyingGlass, ArrowsCounterClockwise,
  CheckCircle, Warning, Robot, PaperPlaneTilt, ArrowLeft,
  ArrowClockwise, Plus, Minus, Equals, Spinner, ChatCircle,
  CalendarBlank, CaretRight, PencilSimple, Trash, X, Check,
  ChatsCircle, FileText, Database, CaretDown, CaretUp, Scales,
  DownloadSimple, FilePdf, FileImage, Table as TableIcon, RepeatOnce,
  FileXls, FileCsv,
} from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../ui/dropdown-menu";
import { toast } from "sonner";
import axios from "axios";
import { API, exportComparisonCsv, exportComparisonExcel } from "../../api";
import MarkdownMessage from "../common/MarkdownMessage";
import HistoryRow, { getItemTitle } from "./HistoryRow";
import ResearchCachePanel from "./ResearchCachePanel";
import EvalDetail from "./EvalDetail";
import CompDetail from "./CompDetail";
import AnalysisDetail from "./AnalysisDetail";

// ── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffDays = Math.floor((Date.now() - d) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}


// ── Chat bubble (used in inline history detail panels) ────────────────────────
function ChatBubble({ msg, enableTableExport }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] ${isUser ? "message-user px-4 py-3" : "message-ai px-4 py-3"}`}>
        {!isUser && (
          <div className="flex items-center gap-2 mb-1">
            <Robot size={13} className="text-primary" />
            <span className="text-xs font-medium text-primary">AI Assistant</span>
          </div>
        )}
        {isUser ? (
          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
        ) : (
          <MarkdownMessage content={msg.content} enableTableExport={enableTableExport} />
        )}
      </div>
    </div>
  );
}


// ── Main component ────────────────────────────────────────────────────────────
export default function HistoryPage({ onOpenItem }) {
  const [view, setView] = useState("list");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [drawingImg, setDrawingImg] = useState(null);
  const [refImg, setRefImg] = useState(null);
  const [latImg, setLatImg] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  // Rename state in detail header
  const [renamingDetail, setRenamingDetail] = useState(false);
  // Re-evaluate state
  const [reEvalTask, setReEvalTask] = useState(null);    // task_id being polled
  const [reEvalStatus, setReEvalStatus] = useState(null); // "processing" | "complete" | "error"
  // Export state
  const [exportDownloading, setExportDownloading] = useState(null); // null | "pdf" | "annotated" | "csv"
  const [exportOpen, setExportOpen] = useState(false);
  const [compareExporting, setCompareExporting] = useState(null); // null | "csv" | "excel"
  const messagesEndRef = useRef(null);

  // ── Load history list ───────────────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const [evRes, coRes, anRes] = await Promise.all([
        axios.get(`${API}/evaluations`),
        axios.get(`${API}/comparisons`),
        axios.get(`${API}/analyses`).catch(() => ({ data: { analyses: [] } })),
      ]);
      const evals    = (evRes.data.evaluations || []).map(e => ({ ...e, _type: "evaluation" }));
      const comps    = (coRes.data.comparisons  || []).map(c => ({ ...c, _type: "comparison" }));
      const analyses = (anRes.data.analyses     || []).map(a => ({ ...a, _type: "analysis" }));
      const merged = [...evals, ...comps, ...analyses]
        .filter(i => i.created_at)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setItems(merged);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // ── Re-evaluate handlers ────────────────────────────────────────────────────
  const handleReopenTool = useCallback(() => {
    if (selected && onOpenItem) onOpenItem(selected);
  }, [selected, onOpenItem]);

  const handleQuickReRun = useCallback(async () => {
    if (!selected) return;
    const sid = selected.session_id || selected.id;
    setReEvalStatus("processing");
    setReEvalTask(null);
    try {
      const res = await axios.post(`${API}/evaluations/${sid}/re-evaluate`);
      const taskId = res.data.task_id;
      setReEvalTask(taskId);

      // Poll task status
      const poll = setInterval(async () => {
        try {
          const t = await axios.get(`${API}/task-status/${taskId}`);
          const st = t.data.status;
          if (st === "complete") {
            clearInterval(poll);
            setReEvalStatus("complete");
            setReEvalTask(null);
            toast.success("Re-evaluation complete — reload history to see updated result");
            loadHistory();
          } else if (st === "error") {
            clearInterval(poll);
            setReEvalStatus("error");
            toast.error(t.data.error || "Re-evaluation failed");
          }
        } catch {
          clearInterval(poll);
          setReEvalStatus("error");
        }
      }, 3000);
    } catch (e) {
      setReEvalStatus("error");
      toast.error(e?.response?.data?.detail || "Failed to start re-evaluation");
    }
  }, [selected, loadHistory]);

  // ── Export helpers ──────────────────────────────────────────────────────────
  const handleExport = useCallback(async (type) => {
    if (!selected) return;
    const sid = selected.session_id || selected.id;
    const urlMap = {
      pdf:       `${API}/download-report/${sid}`,
      annotated: `${API}/download-report/${sid}/annotated`,
      csv:       `${API}/download-report/${sid}/csv`,
    };
    const nameMap = {
      pdf:       `report_${sid.slice(0, 8)}.pdf`,
      annotated: `annotated_${sid.slice(0, 8)}.pdf`,
      csv:       `issues_${sid.slice(0, 8)}.csv`,
    };
    setExportOpen(false);
    setExportDownloading(type);
    try {
      const resp = await axios.get(urlMap[type], { responseType: "blob" });
      const bUrl = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = bUrl; a.download = nameMap[type];
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(bUrl);
      toast.success("Export downloaded");
    } catch {
      toast.error("Export failed — please try again");
    } finally {
      setExportDownloading(null);
    }
  }, [selected]);

  // ── Comparison export (Excel / CSV) for a historical comparison session ────
  const handleCompareExport = useCallback(async (format) => {
    if (!selected) return;
    const sid = selected.session_id || selected.id;
    setCompareExporting(format);
    try {
      const fn = format === "csv" ? exportComparisonCsv : exportComparisonExcel;
      const resp = await fn(sid);
      const mime = format === "csv"
        ? "text/csv"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([resp.data], { type: mime });
      const disposition = resp.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/);
      const fallbackName = `comparison_${sid.slice(0, 8)}.${format === "csv" ? "csv" : "xlsx"}`;

      const bUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = bUrl;
      a.download = match ? match[1] : fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(bUrl);
      toast.success(`Exported as ${format === "csv" ? "CSV" : "Excel"}`);
    } catch (e) {
      let message = e.message;
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          message = JSON.parse(text)?.detail || message;
        } catch (_) { /* keep default message */ }
      } else {
        message = e.response?.data?.detail || message;
      }
      toast.error("Export failed: " + message);
    } finally {
      setCompareExporting(null);
    }
  }, [selected]);

  // ── Open detail ─────────────────────────────────────────────────────────────
  const openDetail = useCallback(async (item) => {
    setSelected(item);
    setView("detail");
    setDetail(null);
    setDrawingImg(null);
    setRefImg(null);
    setLatImg(null);
    setChatMessages([]);
    setRenamingDetail(false);
    setLoadingDetail(true);

    try {
      const sid = item.session_id || item.id;

      if (item._type === "analysis") {
        const [sessRes, chatRes] = await Promise.all([
          axios.get(`${API}/analysis/${sid}`),
          axios.get(`${API}/analysis/${sid}/chat`).catch(() => ({ data: { messages: [] } })),
        ]);
        setDetail(sessRes.data.session);
        setChatMessages(chatRes.data.messages || []);
      } else if (item._type === "comparison") {
        const [compRes, chatRes] = await Promise.all([
          axios.get(`${API}/compare/${sid}`),
          axios.get(`${API}/compare/${sid}/chat`).catch(() => ({ data: { messages: [] } })),
        ]);
        setDetail(compRes.data.comparison);
        setChatMessages(chatRes.data.messages || []);
        Promise.all([
          axios.get(`${API}/compare/${sid}/page/reference/1`).catch(() => null),
          axios.get(`${API}/compare/${sid}/page/latest/1`).catch(() => null),
        ]).then(([refR, latR]) => {
          if (refR) setRefImg(refR.data.image_base64);
          if (latR) setLatImg(latR.data.image_base64);
        });
      } else {
        setDetail(item.evaluation || null);
        const chatRes = await axios.get(`${API}/sessions/${sid}/chat`)
          .catch(() => ({ data: { messages: [] } }));
        setChatMessages(chatRes.data.messages || []);
        axios.get(`${API}/page/${sid}/1`)
          .then(r => setDrawingImg(r.data.image_base64))
          .catch(() => {});
      }
    } catch {
      toast.error("Failed to load session details");
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  // ── Delete item ─────────────────────────────────────────────────────────────
  const deleteItem = useCallback(async (item, e) => {
    e?.stopPropagation();
    const sid = item.session_id || item.id;
    let endpoint;
    if (item._type === "comparison") endpoint = `${API}/comparisons/${sid}`;
    else if (item._type === "analysis") endpoint = `${API}/analysis/${sid}`;
    else endpoint = `${API}/evaluations/${sid}`;
    try {
      await axios.delete(endpoint);
      setItems(prev => prev.filter(i => (i.session_id || i.id) !== sid));
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
  }, []);

  const renameItem = useCallback(async (item, newName) => {
    const sid = item.session_id || item.id;
    let endpoint;
    if (item._type === "comparison") endpoint = `${API}/comparisons/${sid}/name`;
    else if (item._type === "analysis") endpoint = `${API}/analysis/${sid}/name`;
    else endpoint = `${API}/evaluations/${sid}/name`;
    try {
      await axios.patch(endpoint, { name: newName });
      setItems(prev => prev.map(i =>
        (i.session_id || i.id) === sid ? { ...i, display_name: newName } : i
      ));
      if (selected && (selected.session_id || selected.id) === sid) {
        setSelected(prev => ({ ...prev, display_name: newName }));
      }
      toast.success("Renamed");
    } catch {
      toast.error("Failed to rename");
    }
  }, [selected]);

  // ── Send chat ────────────────────────────────────────────────────────────────
  const sendChat = useCallback(async () => {
    const msg = chatInput.trim();
    if (!msg || chatLoading || !selected) return;
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", content: msg }]);
    setChatLoading(true);
    try {
      const sid = selected.session_id || selected.id;
      let endpoint, payload;
      if (selected._type === "comparison") {
        endpoint = `${API}/compare/chat`;
        payload = { session_id: sid, message: msg };
      } else if (selected._type === "analysis") {
        endpoint = `${API}/analysis/chat`;
        payload = { session_id: sid, message: msg };
      } else {
        endpoint = `${API}/chat`;
        payload = { session_id: sid, message: msg };
      }
      const r = await axios.post(endpoint, payload);
      setChatMessages(prev => [...prev, { role: "assistant", content: r.data.response }]);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch {
      toast.error("Failed to send message");
    } finally {
      setChatLoading(false);
    }
  }, [chatInput, chatLoading, selected]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // ── Filtered items ──────────────────────────────────────────────────────────
  const filtered = items.filter(i =>
    filter === "all" ? true : i._type === filter
  );

  // ════════════════════════════════════════════════════════════════════════════
  // LIST VIEW
  // ════════════════════════════════════════════════════════════════════════════
  if (view === "list") {
    return (
      <div className="h-full flex flex-col" data-testid="history-page">
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <ClockCounterClockwise size={16} className="text-primary" />
            <h2 className="font-semibold text-slate-800">History</h2>
            {!loading && filter !== "cache" && (
              <span className="text-xs text-slate-400 bg-slate-200 px-2 py-0.5 rounded-full">
                {filtered.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-slate-100 rounded p-0.5">
              {[
                ["all", "All"],
                ["evaluation", "Evaluations"],
                ["comparison", "Comparisons"],
                ["analysis", "Analyses"],
                ["cache", "Research Cache"],
              ].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setFilter(val)}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors flex items-center gap-1 ${
                    filter === val ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
                  }`}
                  data-testid={`filter-${val}`}
                >
                  {val === "cache" && <Database size={11} />}
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={loadHistory}
              className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-200 rounded transition-colors"
              title="Refresh"
              data-testid="refresh-history"
            >
              <ArrowClockwise size={14} />
            </button>
          </div>
        </div>

        {/* List body */}
        <div className="flex-1 overflow-y-auto">
          {filter === "cache" ? (
            <ResearchCachePanel />
          ) : loading ? (
            <div className="flex items-center justify-center h-40 gap-2 text-slate-400">
              <Spinner size={18} className="animate-spin" />
              <span className="text-sm">Loading history...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-2 text-slate-400">
              <ClockCounterClockwise size={32} className="opacity-40" />
              <p className="text-sm">No {filter !== "all" ? filter + " " : ""}history yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((item) => (
                <HistoryRow
                  key={item.id || item.session_id}
                  item={item}
                  onClick={() => openDetail(item)}
                  onDelete={deleteItem}
                  onRename={renameItem}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ════════════════════════════════════════════════════════════════════════════
  const isComparison = selected?._type === "comparison";
  const isAnalysis   = selected?._type === "analysis";
  const compResult   = detail?.comparison_result || (isComparison ? detail : null);
  const evalResult   = !isComparison && !isAnalysis ? detail : null;
  const detailTitle  = selected ? getItemTitle(selected) : "";

  return (
    <div className="h-full flex flex-col" data-testid="history-detail">

      {/* Detail header */}
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button
            onClick={() => setView("list")}
            className="text-slate-500 hover:text-slate-800 transition-colors flex-shrink-0"
            data-testid="back-to-list"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            {renamingDetail ? (
              <InlineRename
                initialValue={detailTitle}
                onSave={name => { renameItem(selected, name); setRenamingDetail(false); }}
                onCancel={() => setRenamingDetail(false)}
              />
            ) : (
              <div className="flex items-center gap-1.5 group">
                <h2 className="font-semibold text-slate-800 text-sm truncate">{detailTitle}</h2>
                <button
                  onClick={() => setRenamingDetail(true)}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-primary transition-all flex-shrink-0"
                  title="Rename"
                  data-testid="detail-rename-btn"
                >
                  <PencilSimple size={13} />
                </button>
              </div>
            )}
            <div className="flex items-center gap-1.5 mt-0.5">
              <CalendarBlank size={11} className="text-slate-400" />
              <span className="text-xs text-slate-400">{formatDate(selected?.created_at)}</span>
              {isComparison ? (
                <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">Comparison</span>
              ) : isAnalysis ? (
                <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Analysis</span>
              ) : (
                <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">Evaluation</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Action bar (evaluation items only) ─────────────────────────────── */}
      {!isComparison && !isAnalysis && (
        <div className="px-4 py-2.5 border-b border-slate-200 bg-white flex items-center gap-2 flex-wrap">
          {/* Re-evaluate buttons */}
          <button
            onClick={handleReopenTool}
            data-testid="reopen-tool-btn"
            title="Reopen this session in the Evaluate tab"
            className="flex items-center gap-1.5 text-xs text-primary border border-primary/30 hover:bg-primary/5 px-2.5 py-1.5 rounded transition-colors"
          >
            <ArrowClockwise size={13} />
            Reopen in Evaluate
          </button>

          <button
            onClick={handleQuickReRun}
            disabled={reEvalStatus === "processing"}
            data-testid="quick-rerun-btn"
            title="Re-run the compliance check in the background"
            className="flex items-center gap-1.5 text-xs text-slate-700 border border-slate-300 hover:bg-slate-50 px-2.5 py-1.5 rounded transition-colors disabled:opacity-50"
          >
            {reEvalStatus === "processing" ? (
              <Spinner size={13} className="animate-spin" />
            ) : reEvalStatus === "complete" ? (
              <CheckCircle size={13} className="text-green-600" />
            ) : (
              <RepeatOnce size={13} />
            )}
            {reEvalStatus === "processing" ? "Re-evaluating…" : reEvalStatus === "complete" ? "Done" : "Quick Re-run"}
          </button>

          <div className="flex-1" />

          {/* Export dropdown */}
          <div className="relative">
            <button
              onClick={() => setExportOpen(v => !v)}
              disabled={!!exportDownloading}
              data-testid="history-export-btn"
              className="flex items-center gap-1.5 text-xs text-slate-700 border border-slate-300 hover:bg-slate-50 px-2.5 py-1.5 rounded transition-colors"
            >
              {exportDownloading ? (
                <Spinner size={13} className="animate-spin" />
              ) : (
                <DownloadSimple size={13} />
              )}
              Export
              <CaretDown size={11} className={`transition-transform ${exportOpen ? "rotate-180" : ""}`} />
            </button>

            {exportOpen && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-slate-200 rounded shadow-lg z-50">
                <button
                  onClick={() => handleExport("pdf")}
                  data-testid="history-export-pdf"
                  className="flex items-center gap-2.5 w-full px-3 py-2.5 text-xs text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <FilePdf size={14} className="text-primary flex-shrink-0" />
                  <div className="text-left">
                    <div className="font-medium">PDF Report</div>
                    <div className="text-slate-400">Issues, recommendations</div>
                  </div>
                </button>
                <button
                  onClick={() => handleExport("annotated")}
                  data-testid="history-export-annotated"
                  className="flex items-center gap-2.5 w-full px-3 py-2.5 text-xs text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100"
                >
                  <FileImage size={14} className="text-orange-500 flex-shrink-0" />
                  <div className="text-left">
                    <div className="font-medium">PDF with Drawing</div>
                    <div className="text-slate-400">Annotated drawing + report</div>
                  </div>
                </button>
                <button
                  onClick={() => handleExport("csv")}
                  data-testid="history-export-csv"
                  className="flex items-center gap-2.5 w-full px-3 py-2.5 text-xs text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100"
                >
                  <TableIcon size={14} className="text-green-600 flex-shrink-0" />
                  <div className="text-left">
                    <div className="font-medium">Export CSV</div>
                    <div className="text-slate-400">Issues as spreadsheet</div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Action bar (comparison items only) — Export to Excel / CSV ─────── */}
      {isComparison && compResult && (
        <div className="px-4 py-2.5 border-b border-slate-200 bg-white flex items-center justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                disabled={!!compareExporting}
                className="text-xs gap-1.5 border-slate-300"
                data-testid="history-compare-export-button"
              >
                {compareExporting
                  ? <><Spinner size={12} className="animate-spin" /> Exporting...</>
                  : <><DownloadSimple size={14} /> Export <CaretDown size={12} /></>
                }
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => handleCompareExport("excel")}
                disabled={!!compareExporting}
                data-testid="history-compare-export-excel"
              >
                <FileXls size={16} className="text-green-600" />
                Export as Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleCompareExport("csv")}
                disabled={!!compareExporting}
                data-testid="history-compare-export-csv"
              >
                <FileCsv size={16} className="text-slate-500" />
                Export as CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {loadingDetail ? (
        <div className="flex-1 flex items-center justify-center gap-2 text-slate-400">
          <Spinner size={18} className="animate-spin" />
          <span className="text-sm">Loading details...</span>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">

          {/* ── Evaluation detail ── */}
          {!isComparison && !isAnalysis && evalResult && (
            <EvalDetail result={evalResult} drawingImg={drawingImg} />
          )}

          {/* ── Comparison detail ── */}
          {isComparison && compResult && (
            <CompDetail compResult={compResult} refImg={refImg} latImg={latImg} />
          )}

          {/* ── Analysis detail ── */}
          {isAnalysis && detail && (
            <AnalysisDetail session={detail} />
          )}

          {!detail && (
            <div className="p-6 text-center text-slate-400 text-sm">
              No result data available for this session.
            </div>
          )}

          {/* ── Chat history ── */}
          <div className="border-t border-slate-200 px-4 pt-4 pb-2">
            <div className="flex items-center gap-2 mb-3">
              <ChatCircle size={14} className="text-primary" />
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Chat History {chatMessages.length > 0 && `(${chatMessages.length})`}
              </span>
            </div>
            {chatMessages.length === 0 ? (
              <p className="text-xs text-slate-400 italic mb-4">No chat messages yet.</p>
            ) : (
              <div className="space-y-3 mb-4">
                {chatMessages.map((msg, i) => (
                  <ChatBubble key={i} msg={msg} enableTableExport={isAnalysis} />
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Continue chat ── */}
      {!loadingDetail && (
        <div className="qa-input flex-shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <ChatCircle size={14} className="text-primary" />
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
              Continue the conversation
            </span>
          </div>
          <div className="flex gap-2">
            <Input
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
              placeholder={isComparison ? "Ask about these changes..." : isAnalysis ? "Continue your analysis..." : "Ask about this evaluation..."}
              className="flex-1 rounded-sm text-sm"
              disabled={chatLoading}
              data-testid="history-chat-input"
            />
            <Button
              onClick={sendChat}
              disabled={!chatInput.trim() || chatLoading}
              size="sm"
              className="rounded-sm bg-primary hover:bg-primary-hover flex-shrink-0"
              data-testid="history-chat-send"
            >
              {chatLoading
                ? <Spinner size={15} className="animate-spin" />
                : <PaperPlaneTilt size={15} />
              }
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

